import type {
	EvalMeasurement,
	SceneFactCustomerEdit,
	SceneFactProposal,
	SceneFactRetestArmResult,
} from "@sitescene/types";

import {
	SceneFactProposalAction,
	SceneFactProposalSource,
	SceneFactProposalStatus,
	SceneFactRetestArm,
	SceneFactRetestArmStatus,
} from "@sitescene/types";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/badge";
import { ClipPanel } from "@/components/clip-panel";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { Button, Field, TextArea, TextInput } from "@/components/field";
import { JsonBlock } from "@/components/json-block";
import { StatusMessage } from "@/components/status-message";
import {
	plausibilityVerificationPath,
	ROUTES,
	sceneProposalPath,
} from "@/constants/routes";
import {
	apiErrorMessage,
	isConflictError,
	isRetestOverdue,
	isRetestSettled,
	type SceneFactProposalDetail,
	type SceneProposalActionInput,
	useSceneProposal,
	useSceneProposalAction,
	useSceneProposalVideo,
	useStartSceneFactRetest,
} from "@/features/scene-facts/use-scene-proposals";
import { diffWords, hasChanges } from "@/features/scene-facts/word-diff";
import { formatWhen } from "@/pages/plausibility/format";
import { cx } from "@/utils/cx";
import { refId } from "@/utils/ids";

import { FACTS_MAX_CHARS, SOURCE_LABELS, STATUS_LABELS } from "./format";

const {
	Applied,
	Drafting,
	Failed,
	NoChange,
	Pending,
	Rejected,
	Reverted,
	Superseded,
} = SceneFactProposalStatus;

/**
 * Where Redraft and Reject are allowed from, as the service enforces. A
 * stuck draft can be redrafted too; the server says when one is.
 */
const REDRAFTABLE: readonly SceneFactProposalStatus[] = [
	Pending,
	NoChange,
	Failed,
];
const REJECTABLE: readonly SceneFactProposalStatus[] = [
	Drafting,
	Pending,
	NoChange,
	Failed,
];

const ARMS = [
	{ arm: SceneFactRetestArm.Current, title: "Current facts" },
	{ arm: SceneFactRetestArm.Proposed, title: "Proposed facts" },
] as const;

/** Whitespace collapsed, the way the server stores facts. */
const collapse = (text: string) => text.replace(/\s+/g, " ").trim();

/**
 * One scene-fact proposal, laid out for a decision: the clip, what the alert
 * claimed and what the customer changed, the camera's scene reference as it
 * stands, then the proposed change as a diff with the model's reasoning.
 * Staff can edit the text, re-test it on the real detector, and apply,
 * reject or redraft it. The camera's revision history closes the page, with
 * Revert on the revision it is running on.
 */
export const SceneProposalDetail = () => {
	const { proposalId } = useParams();
	const { data, dataUpdatedAt, error, isFetching, isLoading, refetch } =
		useSceneProposal(proposalId);
	const refresh = () => void refetch();

	// Only a first load that failed replaces the page. A background refetch
	// that fails keeps what was last loaded on screen — unmounting it would
	// throw away staff's edited facts and re-sign the clip.
	if (isLoading || !data) {
		return (
			<StatusMessage
				empty="Proposal not found."
				error={error}
				isEmpty={!isLoading && !data}
				isLoading={isLoading}
				loading="Loading proposal…"
			/>
		);
	}

	const { proposal } = data;
	return (
		<div className="flex flex-col gap-4">
			<Link
				className="text-secondary text-sm underline"
				to={ROUTES.ORGANIZATION_SCENE_PROPOSALS}
			>
				← All scene proposals
			</Link>
			{error && (
				<div
					className="bg-warning-primary text-warning-primary flex flex-wrap items-center gap-3 rounded-md p-3 text-sm"
					role="alert"
				>
					<span>
						Could not refresh this proposal: {apiErrorMessage(error)}. Showing
						what was last loaded.
					</span>
					<Button
						disabled={isFetching}
						onClick={refresh}
						type="button"
						variant="secondary"
					>
						Try again
					</Button>
				</div>
			)}

			<div className="grid gap-4 lg:grid-cols-2">
				<Summary detail={data} />
				<Clip proposal={proposal} />
			</div>
			<div className="grid gap-4 lg:grid-cols-2">
				<AlertClaim detail={data} />
				<SceneReference detail={data} />
			</div>
			{/* Keyed on what the draft produced, so a redraft resets the edited
			    text while a re-test or a background refetch leaves it alone. */}
			<Review
				checkedAt={dataUpdatedAt}
				detail={data}
				isFetching={isFetching}
				key={`${proposal.id}:${proposal.status}:${proposal.proposedFacts ?? ""}`}
				onRefresh={refresh}
			/>
			<History detail={data} />
			<JsonBlock label="Full record" value={data} />
		</div>
	);
};

const Summary = ({ detail }: { detail: SceneFactProposalDetail }) => {
	const { event, proposal: p, sensor } = detail;
	return (
		<DetailPanel title="Proposal">
			<KeyValueList
				items={[
					{
						label: "Status",
						value: <Badge label={STATUS_LABELS[p.status]} value={p.status} />,
					},
					{
						label: "Source",
						value: <Badge label={SOURCE_LABELS[p.source]} value={p.source} />,
					},
					{
						label: "Camera",
						value: sensor?.name ?? (
							<code className="text-xs">{refId(p.sensor)}</code>
						),
					},
					{
						label: "Event",
						value: event ? (
							event.title
						) : p.event ? (
							<code className="text-xs">{refId(p.event)}</code>
						) : (
							"—"
						),
					},
					...(p.verification
						? [
								{
									label: "Verification",
									value: (
										<Link
											className="underline"
											to={plausibilityVerificationPath(refId(p.verification))}
										>
											Open verification
										</Link>
									),
								},
							]
						: []),
					{ label: "Created", value: formatWhen(p.createdAt) },
					...(p.requestedBy
						? [
								{
									label: "Requested by",
									value: <code className="text-xs">{p.requestedBy}</code>,
								},
							]
						: []),
					...(p.model ? [{ label: "Drafted with", value: p.model }] : []),
				]}
			/>
			<p className="text-tertiary mt-3 text-xs font-medium">
				{p.source === SceneFactProposalSource.CustomerEdit
					? "Summary of the customer's edit"
					: "Note"}
			</p>
			<p className="text-primary mt-1 whitespace-pre-wrap text-sm italic">
				“{p.note}”
			</p>
		</DetailPanel>
	);
};

const Clip = ({ proposal }: { proposal: SceneFactProposal }) => {
	const video = useSceneProposalVideo(proposal.id);
	return (
		<ClipPanel
			empty="This proposal has no clip attached."
			error={video.error}
			isLoading={video.isLoading}
			mediaKey={proposal.mediaKey}
			testId="scene-proposal-clip"
			video={video.data}
		/>
	);
};

/**
 * What the system said about the clip — the event the customer saw and the
 * detection behind it — and, for a customer-edit proposal, what they changed.
 */
const AlertClaim = ({ detail }: { detail: SceneFactProposalDetail }) => {
	const { claim, event, proposal } = detail;
	return (
		<DetailPanel title="What the alert claimed">
			{!claim && !event ? (
				<p className="text-tertiary text-sm">
					No detection was found for this clip.
				</p>
			) : (
				<KeyValueList
					items={[
						...(event
							? [
									{ label: "Event", value: event.title },
									{
										label: "Event description",
										value: event.description || "—",
									},
									{ label: "Event severity", value: String(event.severity) },
									{ label: "Started", value: formatWhen(event.startTimestamp) },
								]
							: []),
						...(claim
							? [
									{
										label: "Detection",
										value: <code className="text-xs">{claim.type}</code>,
									},
									{
										label: "Detection severity",
										value:
											claim.severity === undefined
												? "—"
												: String(claim.severity),
									},
									{ label: "Labels", value: claim.labels.join(", ") || "—" },
									{
										label: "Detection description",
										value: claim.description ?? "—",
									},
									...(claim.mediaStartTime
										? [
												{
													label: "In clip",
													value: `${claim.mediaStartTime}${claim.mediaEndTime ? ` – ${claim.mediaEndTime}` : ""}`,
												},
											]
										: []),
								]
							: []),
					]}
				/>
			)}
			{proposal.customerEdit && <CustomerEdit edit={proposal.customerEdit} />}
		</DetailPanel>
	);
};

interface EditRow {
	after: string;
	before: string;
	field: string;
}

const CustomerEdit = ({ edit }: { edit: SceneFactCustomerEdit }) => {
	const previous = new Map<string, unknown>(
		Object.entries(edit.previous ?? {}),
	);
	const rows: EditRow[] = Object.entries(edit.changes).map(
		([field, value]) => ({
			after: formatEditValue(value),
			before: formatEditValue(previous.get(field)),
			field,
		}),
	);
	const columns: Column<EditRow>[] = [
		{ header: "Field", key: "field", render: (r) => r.field },
		{
			header: "Before",
			key: "before",
			render: (r) => <span className="text-tertiary">{r.before}</span>,
		},
		{ header: "After", key: "after", render: (r) => r.after },
	];
	return (
		<div className="mt-4">
			<h4 className="text-primary mb-2 text-sm font-semibold">
				The customer's edit{edit.actorName ? ` (${edit.actorName})` : ""}
			</h4>
			<DataTable columns={columns} rowKey={(r) => r.field} rows={rows} />
		</div>
	);
};

const formatEditValue = (value: unknown): string => {
	if (value === undefined || value === null || value === "") return "—";
	return typeof value === "string" ? value : JSON.stringify(value);
};

/** The camera's scene reference today, in the three parts the models receive. */
const SceneReference = ({ detail }: { detail: SceneFactProposalDetail }) => {
	const { sensor } = detail;
	return (
		<DetailPanel title="Camera scene reference">
			{sensor ? (
				<>
					<p className="text-secondary mb-3 text-sm">
						What the first pass and the plausibility check receive today.
						Proposals only ever change the facts.
					</p>
					<KeyValueList
						items={[
							{
								label: "Description",
								value: sensor.sceneDescription || (
									<span className="text-tertiary">none final yet</span>
								),
							},
							{
								label: "Geometry",
								value: sensor.sceneGeometry || (
									<span className="text-tertiary">none</span>
								),
							},
							{
								label: "Facts",
								value: sensor.sceneFacts || (
									<span className="text-tertiary">none taught yet</span>
								),
							},
							...(sensor.sceneFactsUpdatedAt
								? [
										{
											label: "Facts updated",
											value: formatWhen(sensor.sceneFactsUpdatedAt),
										},
									]
								: []),
						]}
					/>
				</>
			) : (
				<p className="text-tertiary text-sm">This camera no longer exists.</p>
			)}
		</DetailPanel>
	);
};

/**
 * The change itself and what staff can do with it. What shows depends on
 * where the proposal is: a draft still running, one that failed or found
 * nothing to learn, one waiting for review, or one already closed.
 *
 * The edited text lives here rather than in the textarea alone because the
 * re-test runs whatever staff are about to apply, edits included.
 */
const Review = ({
	checkedAt,
	detail,
	isFetching,
	onRefresh,
}: {
	/** When the detail was last fetched (epoch ms). */
	checkedAt: number;
	detail: SceneFactProposalDetail;
	isFetching: boolean;
	onRefresh: () => void;
}) => {
	const { proposal: p, sensor, stale, stuck } = detail;
	const action = useSceneProposalAction();
	const [facts, setFacts] = useState(p.proposedFacts ?? "");
	const [reason, setReason] = useState("");

	const act = (input: Omit<SceneProposalActionInput, "proposalId">) =>
		action.mutate({ ...input, proposalId: p.id });
	const redraft = () => act({ action: SceneFactProposalAction.Redraft });
	const canRedraft = REDRAFTABLE.includes(p.status) || stuck;
	const canReject = REJECTABLE.includes(p.status);
	const isPending = p.status === Pending;
	const current = sensor?.sceneFacts ?? p.baseFacts ?? "";
	const edited = facts !== (p.proposedFacts ?? "");
	const changesCamera = collapse(facts) !== collapse(current);
	const overCap = facts.length > FACTS_MAX_CHARS;

	return (
		<>
			<DetailPanel title="Proposed change">
				<div className="flex flex-col gap-3">
					{p.status === Drafting && !stuck && (
						<p className="text-secondary text-sm" role="status">
							Drafting… The model is reading the clip and the note. This page
							refreshes on its own when the draft is ready.
						</p>
					)}
					{p.status === Drafting && stuck && (
						<div className="flex flex-wrap items-center gap-3" role="alert">
							<p className="text-warning-primary text-sm">
								This draft appears stuck: it has not finished in over half an
								hour, so the worker most likely failed. Redraft to queue it
								again, or reject it.
							</p>
							<Button onClick={onRefresh} type="button" variant="secondary">
								Check again
							</Button>
						</div>
					)}
					{p.status === Failed && (
						<>
							<p className="text-error-primary text-sm" role="alert">
								The draft failed{p.error ? `: ${p.error}` : "."}
							</p>
							<p className="text-secondary text-sm">
								Redraft to try again, or reject it.
							</p>
						</>
					)}
					{p.status === NoChange && (
						<>
							<p className="text-secondary text-sm">
								Nothing to change: the model found nothing about the place
								itself in the note.
							</p>
							{p.reasoning && (
								<p className="text-primary text-sm italic">“{p.reasoning}”</p>
							)}
						</>
					)}
					{isPending && (
						<>
							{stale && (
								<p
									className="bg-warning-primary text-warning-primary rounded-md p-3 text-sm"
									role="alert"
								>
									The camera's facts changed after this was drafted, so it
									cannot be applied as it is. Redraft it against the current
									facts.
								</p>
							)}
							<FactsDiff after={facts} before={current} />
							{p.reasoning && (
								<p className="text-secondary text-sm">
									<span className="text-tertiary">Why: </span>
									{p.reasoning}
								</p>
							)}
							<Field htmlFor="proposal-facts" label="Facts to apply (editable)">
								<TextArea
									id="proposal-facts"
									onChange={(e) => setFacts(e.target.value)}
									rows={5}
									value={facts}
								/>
							</Field>
							<p
								className={cx(
									"text-xs",
									overCap ? "text-warning-primary" : "text-tertiary",
								)}
							>
								{facts.length} / {FACTS_MAX_CHARS} characters
								{overCap
									? " — over what a camera holds; shorten it to apply or re-test."
									: "."}{" "}
								Describe the place only: the text is checked again for
								instructions and alert language when it is applied.
								{!changesCamera &&
									" It matches the camera's facts as they are."}
							</p>
						</>
					)}
					{(p.status === Applied || p.status === Reverted) && (
						<AppliedRevision proposal={p} />
					)}
					{(p.status === Rejected || p.status === Superseded) && (
						<ClosedProposal proposal={p} />
					)}

					{(canRedraft || canReject) && (
						<div className="flex flex-wrap items-end gap-3">
							{isPending && (
								<Button
									disabled={
										action.isPending || stale || !changesCamera || overCap
									}
									onClick={() =>
										act({
											action: SceneFactProposalAction.Apply,
											facts: edited ? facts : undefined,
										})
									}
									type="button"
								>
									Apply to camera
								</Button>
							)}
							{canRedraft && (
								<Button
									disabled={action.isPending}
									onClick={redraft}
									type="button"
									variant="secondary"
								>
									Redraft
								</Button>
							)}
							{canReject && (
								<>
									<Field htmlFor="reject-reason" label="Reason (optional)">
										<TextInput
											id="reject-reason"
											onChange={(e) => setReason(e.target.value)}
											value={reason}
										/>
									</Field>
									<Button
										disabled={action.isPending}
										onClick={() =>
											act({
												action: SceneFactProposalAction.Reject,
												reason: reason.trim() || undefined,
											})
										}
										type="button"
										variant="danger"
									>
										Reject
									</Button>
								</>
							)}
						</div>
					)}
					{action.error && (
						<div className="flex flex-wrap items-center gap-3" role="alert">
							<span className="text-error-primary text-sm">
								{apiErrorMessage(action.error)}
							</span>
							{isConflictError(action.error) &&
								canRedraft &&
								action.variables?.action !==
									SceneFactProposalAction.Redraft && (
									<Button
										disabled={action.isPending}
										onClick={redraft}
										type="button"
										variant="secondary"
									>
										Redraft now
									</Button>
								)}
						</div>
					)}
				</div>
			</DetailPanel>
			{(isPending || p.retest) && (
				<Retest
					checkedAt={checkedAt}
					edited={edited}
					facts={facts}
					isFetching={isFetching}
					onRefresh={onRefresh}
					proposal={p}
				/>
			)}
		</>
	);
};

const AppliedRevision = ({ proposal: p }: { proposal: SceneFactProposal }) => (
	<>
		<p className="text-secondary text-sm">
			Applied {formatWhen(p.appliedAt)}
			{p.appliedBy ? ` by ${p.appliedBy}` : ""}.
			{p.status === Reverted &&
				" Reverted since; the camera history below shows what replaced it."}
			{p.revertOf && (
				<>
					{" "}
					Undid{" "}
					<Link className="underline" to={sceneProposalPath(refId(p.revertOf))}>
						an earlier revision
					</Link>
					.
				</>
			)}
		</p>
		<FactsDiff after={p.appliedFacts ?? ""} before={p.previousFacts ?? ""} />
	</>
);

const ClosedProposal = ({ proposal: p }: { proposal: SceneFactProposal }) => (
	<>
		<p className="text-secondary text-sm">
			{p.status === Rejected
				? `Rejected ${formatWhen(p.rejectedAt)}${p.rejectedBy ? ` by ${p.rejectedBy}` : ""}${p.rejectedReason ? `: “${p.rejectedReason}”` : "."}`
				: "A later edit of the same event replaced this proposal."}
		</p>
		{p.proposedFacts && (
			<>
				<p className="text-tertiary text-xs font-medium">What it proposed</p>
				<FactsDiff after={p.proposedFacts} before={p.baseFacts ?? ""} />
			</>
		)}
	</>
);

/** Word diff of the camera's facts against what would replace them. */
const FactsDiff = ({ after, before }: { after: string; before: string }) => {
	const segments = diffWords(before, after);

	if (!before && !after) {
		return <p className="text-tertiary text-sm">No facts on either side.</p>;
	}
	if (!hasChanges(segments)) {
		return (
			<p className="text-primary text-sm">
				<span className="text-tertiary">Unchanged: </span>
				{after}
			</p>
		);
	}

	return (
		<div>
			<p
				className="text-primary whitespace-pre-wrap text-sm leading-6"
				data-testid="facts-diff"
			>
				{segments.map((segment, index) =>
					segment.kind === "added" ? (
						<ins
							className="bg-success-primary text-success-primary no-underline"
							key={index}
						>
							{segment.text}
						</ins>
					) : segment.kind === "removed" ? (
						<del className="bg-error-primary text-error-primary" key={index}>
							{segment.text}
						</del>
					) : (
						<span key={index}>{segment.text}</span>
					),
				)}
			</p>
			<p className="text-tertiary mt-1 text-xs">
				{before
					? "Against the camera's current facts: "
					: "The camera has no facts yet, so all of it is new: "}
				<ins className="bg-success-primary text-success-primary no-underline">
					added
				</ins>{" "}
				<del className="bg-error-primary text-error-primary">removed</del>
			</p>
		</div>
	);
};

/**
 * Before/after on the real first-pass worker: the same clip and the camera's
 * real config, once with today's scene reference and once with the facts
 * staff are about to apply. The latest run lives on the proposal, so it
 * reads back after a reload and lands through the detail's own poll. A
 * failed poll is the page banner's to report, once, not this panel's too.
 */
const Retest = ({
	checkedAt,
	edited,
	facts,
	isFetching,
	onRefresh,
	proposal: p,
}: {
	/** When the proposal detail was last fetched (epoch ms). */
	checkedAt: number;
	edited: boolean;
	facts: string;
	isFetching: boolean;
	onRefresh: () => void;
	proposal: SceneFactProposal;
}) => {
	const start = useStartSceneFactRetest(p.id);
	const { retest } = p;
	const settled = retest ? isRetestSettled(retest) : false;
	// Polling stops once overdue, so the page says so and offers a manual check.
	const overdue = retest ? isRetestOverdue(retest, checkedAt) : false;
	const canRun = p.status === Pending;
	// The API refuses over-long facts rather than trimming them.
	const overCap = facts.length > FACTS_MAX_CHARS;

	return (
		<DetailPanel title="Re-test on the real detector">
			<p className="text-secondary text-sm">
				Runs this clip through the production first pass twice with this
				camera's real config: once with today's scene reference, once with the{" "}
				{edited ? "edited" : "proposed"} facts in its place. Results usually
				land within a couple of minutes.
			</p>
			{canRun && (
				<div className="mt-3">
					<Button
						disabled={start.isPending || overCap}
						onClick={() => start.mutate({ facts: edited ? facts : undefined })}
						type="button"
						variant="secondary"
					>
						{start.isPending
							? "Starting…"
							: retest
								? "Run again"
								: "Run re-test"}
					</Button>
				</div>
			)}
			{start.error && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{apiErrorMessage(start.error)}
				</p>
			)}
			{retest && (
				<>
					<p className="text-tertiary mt-3 text-xs">
						Started {formatWhen(retest.requestedAt)}
						{settled
							? "."
							: overdue
								? " — still no result after 15 minutes, so the run may have been lost."
								: " — waiting for results…"}
					</p>
					<div className="mt-2 grid gap-4 lg:grid-cols-2">
						{ARMS.map(({ arm, title }) => (
							<ArmCard
								key={arm}
								result={retest.arms.find((a) => a.arm === arm)}
								title={title}
							/>
						))}
					</div>
				</>
			)}
			{overdue && (
				<div className="mt-3">
					<Button
						disabled={isFetching}
						onClick={onRefresh}
						type="button"
						variant="secondary"
					>
						Check again
					</Button>
				</div>
			)}
			<p className="text-tertiary mt-3 text-xs">
				A single sample per arm. Model output varies from run to run, so read
				this as a sanity check on the proposal, not a measurement of it.
			</p>
		</DetailPanel>
	);
};

const ArmCard = ({
	result,
	title,
}: {
	result: SceneFactRetestArmResult | undefined;
	title: string;
}) => {
	const meta = result
		? [
				result.firstPassModel,
				typeof result.costUsd === "number"
					? `$${result.costUsd.toFixed(4)}`
					: undefined,
			].filter(Boolean)
		: [];
	return (
		<section
			aria-label={title}
			className="border-secondary rounded-md border p-3"
		>
			<div className="flex items-center justify-between gap-2">
				<h4 className="text-primary text-sm font-semibold">{title}</h4>
				<Badge value={result?.status} />
			</div>
			{!result ? (
				<p className="text-tertiary mt-2 text-sm">Not recorded.</p>
			) : result.status === SceneFactRetestArmStatus.Failed ? (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{result.error ?? "The run failed."}
				</p>
			) : (
				<>
					{result.status === SceneFactRetestArmStatus.Pending && (
						<p className="text-secondary mt-2 text-sm">Running…</p>
					)}
					{(result.status === SceneFactRetestArmStatus.Completed ||
						result.measurements.length > 0) && (
						<Measurements measurements={result.measurements} />
					)}
				</>
			)}
			{meta.length > 0 && (
				<p className="text-tertiary mt-2 text-xs">{meta.join(" · ")}</p>
			)}
			{result?.sceneReference !== undefined && (
				<div className="mt-2">
					<JsonBlock
						label="Scene reference sent"
						value={result.sceneReference}
					/>
				</div>
			)}
		</section>
	);
};

const Measurements = ({
	measurements,
}: {
	measurements: EvalMeasurement[];
}) => {
	if (measurements.length === 0) {
		return <p className="text-primary mt-2 text-sm">No detections.</p>;
	}
	const highest = Math.max(...measurements.map((m) => m.severity ?? 0));
	return (
		<>
			<p className="text-primary mt-2 text-sm">
				Highest severity <strong className="tabular-nums">{highest}</strong> ·{" "}
				{measurements.length} detection{measurements.length === 1 ? "" : "s"}
			</p>
			<ul className="mt-2 flex flex-col gap-2">
				{measurements.map((m, index) => (
					<li className="text-sm" key={index}>
						<span className="font-semibold tabular-nums">
							{m.severity ?? "—"}
						</span>{" "}
						<code className="text-xs">{m.type}</code>
						{m.description && <p className="text-secondary">{m.description}</p>}
					</li>
				))}
			</ul>
		</>
	);
};

/**
 * Every revision applied to this camera, most recently applied first. Only
 * the one the camera is running on can be reverted: undoing an older one
 * would discard every revision applied since.
 */
const History = ({ detail }: { detail: SceneFactProposalDetail }) => {
	const { history, proposal, sensor } = detail;
	return (
		<DetailPanel title="Camera history">
			{history.length === 0 ? (
				<p className="text-tertiary text-sm">
					No facts have been applied to this camera yet.
				</p>
			) : (
				<ol className="flex flex-col">
					{history.map((revision) => {
						const isCurrent = revision.id === sensor?.currentProposalId;
						return (
							<li
								className="border-secondary border-b py-3 first:pt-0 last:border-b-0 last:pb-0"
								key={revision.id}
							>
								<div className="flex flex-wrap items-center gap-2 text-sm">
									<span className="text-primary">
										{formatWhen(revision.appliedAt)}
									</span>
									<Badge
										label={SOURCE_LABELS[revision.source]}
										value={revision.source}
									/>
									<Badge
										label={STATUS_LABELS[revision.status]}
										value={revision.status}
									/>
									{isCurrent && (
										<span className="text-brand-800 text-xs font-semibold">
											current
										</span>
									)}
									{revision.id === proposal.id ? (
										<span className="text-tertiary text-xs">this proposal</span>
									) : (
										<Link
											className="text-secondary text-xs underline"
											to={sceneProposalPath(revision.id)}
										>
											open
										</Link>
									)}
									{isCurrent && revision.status === Applied && (
										<RevertControl revision={revision} />
									)}
								</div>
								<p className="text-primary mt-1 text-sm">
									{revision.appliedFacts || (
										<span className="text-tertiary italic">
											No facts: this revision cleared them.
										</span>
									)}
								</p>
							</li>
						);
					})}
				</ol>
			)}
		</DetailPanel>
	);
};

/** Revert asks once more, naming what the camera goes back to. */
const RevertControl = ({ revision }: { revision: SceneFactProposal }) => {
	const [confirming, setConfirming] = useState(false);
	const action = useSceneProposalAction();

	if (!confirming) {
		return (
			<Button
				className="ml-auto"
				onClick={() => setConfirming(true)}
				type="button"
				variant="secondary"
			>
				Revert
			</Button>
		);
	}

	return (
		<span className="ml-auto flex flex-wrap items-center gap-2">
			<span className="text-secondary text-xs">
				{revision.previousFacts
					? "Restore the facts this revision replaced?"
					: "The camera had no facts before this revision. Clear them?"}
			</span>
			<Button
				disabled={action.isPending}
				onClick={() =>
					action.mutate(
						{
							action: SceneFactProposalAction.Revert,
							proposalId: revision.id,
						},
						{ onSuccess: () => setConfirming(false) },
					)
				}
				type="button"
			>
				Confirm revert
			</Button>
			<Button
				onClick={() => setConfirming(false)}
				type="button"
				variant="secondary"
			>
				Cancel
			</Button>
			{action.error && (
				<span className="text-error-primary text-xs" role="alert">
					{apiErrorMessage(action.error)}
				</span>
			)}
		</span>
	);
};
